import { useMemo, useState } from 'react';
import { Globe, RefreshCw, AlertTriangle, MapPin } from 'lucide-react';
import { useAuth } from '../lib/auth-context';
import { useThreatFeed } from '../lib/hooks';
import { Globe3D, type ThreatPoint } from '../components/Globe3D';
import { Starfield } from '../components/Starfield';
import { resolveThreatGeo } from '../lib/geo';

interface ThreatLocation {
  id: string;
  lat: number;
  lng: number;
  city: string;
  country: string;
  threatLevel: string;
  confidence: number;
  count: number;
  precision: 'city' | 'country';
}

/**
 * Tenant-scoped attack origins map.
 * Uses the same geo atlas as Overview (packages/dashboard/src/lib/geo.ts) —
 * no Nominatim / dual city tables.
 */
export function ThreatMap() {
  const { isConnected, tenant } = useAuth();
  const { data, isLoading, refetch } = useThreatFeed();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [focusToken, setFocusToken] = useState(0);

  const tenantLabel = tenant?.name || 'this project';

  const { locations, unmappedCount } = useMemo(() => {
    const threats = data?.threats ?? [];
    const locationMap = new Map<string, ThreatLocation>();
    let unmapped = 0;

    for (const threat of threats) {
      const city = threat.sourceCity || '';
      const country = threat.sourceCountry || '';
      const geo = resolveThreatGeo(city, country);
      if (!geo.coords) {
        unmapped++;
        continue;
      }

      const key = `${city || country || 'unknown'}|${geo.coords.lat.toFixed(3)},${geo.coords.lng.toFixed(3)}`;
      const existing = locationMap.get(key);
      if (existing) {
        existing.count += 1;
        existing.confidence = Math.max(existing.confidence, threat.confidence || 0);
        if (threat.level === 'critical' || (threat.level === 'high' && existing.threatLevel !== 'critical')) {
          existing.threatLevel = threat.level;
        }
      } else {
        locationMap.set(key, {
          id: key,
          lat: geo.coords.lat,
          lng: geo.coords.lng,
          city: city || (geo.precision === 'country' ? country : 'Unknown'),
          country: country || '??',
          threatLevel: threat.level || 'low',
          confidence: threat.confidence || 0,
          count: 1,
          precision: geo.precision === 'city' ? 'city' : 'country',
        });
      }
    }

    return {
      locations: Array.from(locationMap.values()).sort((a, b) => b.count - a.count),
      unmappedCount: unmapped,
    };
  }, [data?.threats]);

  const globePoints: ThreatPoint[] = useMemo(
    () =>
      locations.map((l) => ({
        id: l.id,
        lat: l.lat,
        lng: l.lng,
        level: (l.threatLevel === 'none' ? 'low' : l.threatLevel) as ThreatPoint['level'],
        count: l.count,
        city: l.city,
        country: l.country,
        label: `${l.count} threat${l.count === 1 ? '' : 's'}`,
      })),
    [locations],
  );

  const maxCount = locations.length ? Math.max(...locations.map((l) => l.count)) : 1;

  if (!isConnected) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <h2 className="text-xl sm:text-2xl font-bold text-white mb-6">Attack origins</h2>
        <div className="bg-white/[0.015] border border-white/[0.04] rounded-2xl p-12 text-center">
          <Globe className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500">Connect to the control plane to view attack origins for this project.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[100vw] overflow-x-hidden">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6 sm:mb-8">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-white">Attack origins</h2>
          <p className="text-gray-400 mt-1 text-sm sm:text-base">
            Geographic view of threats for{' '}
            <span className="text-white font-medium">{tenantLabel}</span>
            <span className="text-gray-600"> — not a global SOC map</span>
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => refetch()}
            className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/5"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-red-500/10 border border-red-500/20 rounded-lg">
            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-red-400 text-sm font-medium">Live · 5s</span>
          </div>
        </div>
      </div>

      <div className="bg-white/[0.015] border border-white/[0.04] rounded-2xl overflow-hidden mb-6 backdrop-blur-sm">
        <div className="p-4 border-b border-white/[0.05] flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-200 uppercase tracking-wider">Threat map</h3>
            <p className="text-[11px] text-gray-500 mt-0.5">
              {globePoints.length} mapped location{globePoints.length === 1 ? '' : 's'}
              {unmappedCount > 0 ? ` · ${unmappedCount} unmapped` : ''}
              {' · '}same atlas as Overview
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              const el = document.getElementById('globe-threatmap');
              if (!el) return;
              if (document.fullscreenElement) document.exitFullscreen();
              else el.requestFullscreen();
            }}
            className="p-2 hover:bg-white/5 rounded-lg transition-colors text-gray-400 hover:text-white shrink-0"
            title="Toggle fullscreen"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
          </button>
        </div>
        <div id="globe-threatmap" className="relative h-[320px] sm:h-[480px] lg:h-[600px]" style={{ background: '#000' }}>
          <Starfield />
          <style>{`
            #globe-threatmap:fullscreen {
              height: 100vh !important;
              display: flex;
              align-items: center;
              justify-content: center;
              background: #000 !important;
            }
          `}</style>
          <Globe3D
            points={globePoints}
            selectedId={selectedId}
            focusToken={focusToken}
            onPointClick={(p) => {
              setSelectedId((prev) => (prev === p.id ? null : p.id));
              setFocusToken((n) => n + 1);
            }}
            centerLat={30}
            centerLng={-40}
            className="w-full h-full"
          />
          {locations.length === 0 && !isLoading && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center px-4">
                <AlertTriangle className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                <p className="text-gray-500">No mappable threats for this project yet</p>
                <p className="text-gray-600 text-sm mt-1">
                  Threats need a city or country in telemetry to plot
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
        {[
          { level: 'critical', color: 'bg-red-600', desc: 'Active exploitation' },
          { level: 'high', color: 'bg-orange-500', desc: 'Scanner / attack' },
          { level: 'medium', color: 'bg-yellow-400', desc: 'Suspicious activity' },
          { level: 'low', color: 'bg-green-500', desc: 'Reconnaissance' },
        ].map((item) => (
          <div
            key={item.level}
            className="bg-white/[0.015] border border-white/[0.04] rounded-xl p-4"
          >
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${item.color}`} />
              <div>
                <div className="text-white text-sm font-medium capitalize">{item.level}</div>
                <div className="text-gray-500 text-xs">{item.desc}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white/[0.015] border border-white/[0.04] rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-white/[0.05]">
          <h3 className="text-sm font-semibold text-gray-200 uppercase tracking-wider">
            Top attack locations
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px]">
            <thead>
              <tr className="border-b border-white/[0.06]">
                <th className="text-left p-3 sm:p-4 text-sm font-medium text-gray-400">Location</th>
                <th className="text-left p-3 sm:p-4 text-sm font-medium text-gray-400">Pin</th>
                <th className="text-left p-3 sm:p-4 text-sm font-medium text-gray-400">Level</th>
                <th className="text-left p-3 sm:p-4 text-sm font-medium text-gray-400">Conf</th>
                <th className="text-left p-3 sm:p-4 text-sm font-medium text-gray-400">Count</th>
              </tr>
            </thead>
            <tbody>
              {locations.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-12 text-center text-gray-500">
                    {isLoading ? 'Loading threat data…' : 'No attack locations detected'}
                  </td>
                </tr>
              ) : (
                locations.map((loc) => {
                  const selected = selectedId === loc.id;
                  return (
                    <tr
                      key={loc.id}
                      className={`border-b border-white/[0.04] transition-colors cursor-pointer ${
                        selected ? 'bg-indigo-500/10' : 'hover:bg-white/[0.03]'
                      }`}
                      onClick={() => {
                        setSelectedId((prev) => (prev === loc.id ? null : loc.id));
                        setFocusToken((n) => n + 1);
                      }}
                    >
                      <td className="p-3 sm:p-4">
                        <div className="flex items-center gap-2">
                          <MapPin className={`w-3.5 h-3.5 shrink-0 ${selected ? 'text-indigo-400' : 'text-gray-500'}`} />
                          <div>
                            <div className="text-white text-sm font-medium">{loc.city}</div>
                            <div className="text-gray-500 text-xs">{loc.country}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-3 sm:p-4 text-sm text-gray-400 font-mono">
                        {loc.lat.toFixed(2)}, {loc.lng.toFixed(2)}
                        {loc.precision === 'country' && (
                          <span className="block text-[10px] text-amber-500/80">country centroid</span>
                        )}
                      </td>
                      <td className="p-3 sm:p-4">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            loc.threatLevel === 'critical'
                              ? 'bg-red-900/30 text-red-400'
                              : loc.threatLevel === 'high'
                                ? 'bg-orange-900/30 text-orange-400'
                                : loc.threatLevel === 'medium'
                                  ? 'bg-yellow-900/30 text-yellow-400'
                                  : 'bg-green-900/30 text-green-400'
                          }`}
                        >
                          {loc.threatLevel.toUpperCase()}
                        </span>
                      </td>
                      <td className="p-3 sm:p-4 text-sm text-gray-300 tabular-nums">{loc.confidence}%</td>
                      <td className="p-3 sm:p-4">
                        <div className="flex items-center gap-2">
                          <div className="text-white text-sm font-medium tabular-nums">{loc.count}</div>
                          <div className="h-2 flex-1 bg-black/40 rounded-full overflow-hidden max-w-[100px]">
                            <div
                              className="h-full bg-red-500/80"
                              style={{ width: `${Math.min((loc.count / maxCount) * 100, 100)}%` }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
